/**
 * Newsletter incentive line on the PDP.
 *
 * Keeps the "get it for X" price in step with the selected variant and opens
 * the Klaviyo form for the current language instead of navigating away. The
 * anchor keeps a real href, so if Klaviyo's onsite script never arrives the
 * click still lands on the subscribe page.
 *
 * Klaviyo fires a `klaviyoForms` event on submit, so the call to action is
 * swapped for a confirmation. That confirmation is feedback on the submit that
 * just happened, and nothing more. The theme cannot see Klaviyo issue a code,
 * cannot see it redeemed — the code is random per subscriber and spent inside
 * checkout — and a per-browser flag never meant "this person" anyway. So it is
 * deliberately short-lived. Real subscription state, for a logged-in customer,
 * comes from Shopify in the Liquid: consent, tags, or a past order.
 */
const SUBSCRIBED_KEY = 'hurley:newsletter-subscribed';
/** Matches the theme's mobile breakpoint (750), so the split lands where every other one does. */
const MOBILE_MQ = '(max-width: 749px)';

/** An earlier build kept this for 30 days, which asserted far more than we know. */
function dropLegacyFlag() {
  try {
    window.localStorage.removeItem(SUBSCRIBED_KEY);
  } catch (error) {
    /* Blocked storage: nothing to clean up. */
  }
}

function subscribedAt() {
  try {
    const stamp = parseInt(window.sessionStorage.getItem(SUBSCRIBED_KEY), 10);
    return Number.isFinite(stamp) ? stamp : null;
  } catch (error) {
    return null;
  }
}

/** `minutes` of 0 keeps the confirmation for the rest of the session. */
function hasSubscribed(minutes) {
  const stamp = subscribedAt();
  if (stamp === null) return false;
  if (!minutes) return true;
  return Date.now() - stamp < minutes * 60000;
}

function rememberSubscribed() {
  try {
    window.sessionStorage.setItem(SUBSCRIBED_KEY, String(Date.now()));
  } catch (error) {
    /* Private mode or blocked storage: the confirmation just will not persist. */
  }
}

if (!customElements.get('newsletter-incentive')) {
  class NewsletterIncentive extends HTMLElement {
    constructor() {
      super();
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onCtaClick = this.onCtaClick.bind(this);
      this.onKlaviyoForm = this.onKlaviyoForm.bind(this);
    }

    connectedCallback() {
      this.textEl = this.querySelector('[data-incentive-text]');
      this.ctaEl = this.querySelector('[data-incentive-cta]');
      this.template = this.dataset.textTemplate || '';
      this.hideOnSale = this.dataset.hideOnSale === 'true';
      this.formId = this.dataset.klaviyoFormId || '';
      this.mobileFormId = this.dataset.klaviyoFormIdMobile || '';
      this.prices = this.readPrices();

      this.confirmEl = this.querySelector('[data-incentive-confirm]');

      this.ctaEl?.addEventListener('click', this.onCtaClick);
      window.addEventListener('klaviyoForms', this.onKlaviyoForm);

      dropLegacyFlag();
      this.confirmMinutes = parseInt(this.dataset.confirmMinutes, 10) || 0;
      if (hasSubscribed(this.confirmMinutes)) this.showConfirmation();

      if (theme.pubsub) {
        this.unsubscribeVariant = theme.pubsub.subscribe(
          theme.pubsub.events.VARIANT_CHANGE,
          this.onVariantChange
        );
      }
    }

    disconnectedCallback() {
      this.ctaEl?.removeEventListener('click', this.onCtaClick);
      window.removeEventListener('klaviyoForms', this.onKlaviyoForm);
      this.unsubscribeVariant?.();
    }

    readPrices() {
      const node = this.querySelector('[data-incentive-prices]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return {};
      }
    }

    onVariantChange(data) {
      const variant = data?.variant;
      if (!variant) {
        this.hidden = true;
        return;
      }

      const entry = this.prices[String(variant.id)];
      if (!entry) {
        this.hidden = true;
        return;
      }

      if (!entry.available || (this.hideOnSale && entry.onSale)) {
        this.hidden = true;
        return;
      }

      if (this.textEl && this.template) {
        this.textEl.textContent = this.template.replace('__PRICE__', entry.price);
      }
      this.hidden = false;
    }

    onKlaviyoForm(event) {
      const detail = event?.detail;
      // `redactedSubmit` is the same success with the payload withheld.
      if (!detail || (detail.type !== 'submit' && detail.type !== 'redactedSubmit')) return;
      // Another form on the page signing someone up is not our offer. Either
      // twin is ours, so match the pair rather than the one we would open now.
      const ours = [this.formId, this.mobileFormId].filter(Boolean);
      if (ours.length && detail.formId && !ours.includes(detail.formId)) return;

      rememberSubscribed();
      this.showConfirmation();
    }

    showConfirmation() {
      if (this.ctaEl) this.ctaEl.hidden = true;
      if (this.confirmEl) this.confirmEl.hidden = false;
    }

    /**
     * Klaviyo designs a pop-up per device and gives each its own id, so the
     * right one depends on the viewport at the moment of the click - not at
     * load, or a rotate would open the other device's layout. Either id on
     * its own still works everywhere: openForm ignores Klaviyo's own device
     * targeting, so a missing twin falls back rather than dead-clicking.
     */
    currentFormId() {
      const isMobile = window.matchMedia(MOBILE_MQ).matches;
      return isMobile ? this.mobileFormId || this.formId : this.formId || this.mobileFormId;
    }

    onCtaClick(event) {
      const formId = this.currentFormId();
      if (!formId) return;
      if (typeof window._klOnsite === 'undefined') return;

      event.preventDefault();
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
    }
  }

  customElements.define('newsletter-incentive', NewsletterIncentive);
}
